import React, { useState, useEffect } from 'react';
import { Form, InputGroup, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import NotificationContainer from '../components/notifications';
import EntitySubscriptionsList from '../components/notifications/EntitySubscriptionList';
import { IAuthContext } from '../context/auth/auth-context';
import withAuthContext from '../context/auth/AuthConsumer';
import NOTIFICATION_TYPE from '../types/notification-type';
import { CeresLogger } from '../logger';
import EmailNotification from '../components/notifications/EmailNotification';
import Filter from '../components/notifications/Filter';
import { API_BASE_URL } from '../util/api.util';
import ReactGA from 'react-ga4';
import { Button as UiButton, Card, Heading, Kicker as UiKicker } from '../components/ui';

const PageRoot = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const Inner = styled.div`
  margin: 4em 0;
  padding: 0 4em 4em;
  overflow: auto;
  height: 100vh;
  box-sizing: border-box;

  form {
    margin-top: 20px;
  }
`;

const GlassCard = styled(Card)`
  padding: 1.35rem 1.35rem 1.5rem;

  .form-control {
    background: rgba(15, 23, 42, 0.55);
    border: 1px solid rgba(148, 163, 184, 0.2);
    color: #f4f4f5;

    &::placeholder {
      color: rgba(212, 212, 216, 0.55);
    }

    &:focus {
      background: rgba(15, 23, 42, 0.65);
      border-color: rgba(52, 211, 153, 0.35);
      color: #f4f4f5;
      box-shadow: 0 0 0 2px rgba(52, 211, 153, 0.15);
    }
  }

  .invalid-feedback {
    color: #fca5a5;
  }
`;


const SubscriptionTypeGroup = styled(Form.Group)`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  align-items: center;
  margin: 1rem 0 1.25rem;

  .input-group-text {
    padding: 0.375rem 0;
    border: none;
    background: none;
    display: inline;
    margin-right: 5px;
  }
`;

const SubscriptionTypeRadioItem = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  cursor: pointer;
  color: #e2e8f0;
  font-size: 15px;
  font-weight: 500;
`;

const SubscriptionTypeText = styled.span``;

const SubscriptionListLink = styled.a`
  margin-top: 14px;
  display: inline-block;
  color: #5eead4;
  font-weight: 600;
  font-size: 15px;

  &:hover {
    color: #34d399;
  }
`;

const TrackingList = styled.div`
  margin-top: 20px;
`;

const TrackbotRobo = styled.div`
  border: 1px solid rgba(52, 211, 153, 0.25);
  border-radius: 20px;
  padding: 1rem 1rem 1.25rem;
  width: 100%;
  max-width: 200px;
  margin: 0.75rem auto 0;
  background: rgba(15, 23, 42, 0.45);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.25);

  & h4 {
    text-align: center;
    font-size: 1.1rem;
    font-weight: 700;
    color: #f8fafc;
    margin: 0.5rem 0 0;
  }

  & img {
    width: 100%;
    display: block;
  }
`;

const Trackbot = styled.div`
  text-align: center;

  h3 {
    color: #34d399;
    text-align: center;
    font-size: clamp(1.5rem, 3vw, 1.85rem);
    font-weight: 600;
    font-family: 'Instrument Serif', Georgia, serif;
    font-style: italic;
    margin: 0 0 0.25rem;
  }
`;

const CustRow = styled(Row)`
  align-items: flex-start;
`;

const ContentHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;

  h3 {
    margin: 0;
    color: #fafafa;
    font-size: clamp(1.35rem, 2.5vw, 1.6rem);
    font-weight: 400;
    font-family: 'Instrument Serif', Georgia, serif;
  }
`;

const FilterArea = styled.div`
  max-width: 520px;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 10px 16px;

  & > span {
    font-weight: 600;
    color: #94a3b8;
    font-size: 14px;
  }
`;

type SortOrder = 'asc' | 'desc';

const NotificationPage: React.FC<{ context: IAuthContext }> = (props) => {
  const context = props.context;
  const userEmail = context.getUserEmail();
  const [isFormValidated, setFormValidation] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSubscriptionList, setShowSubscriptionList] = useState(false);
  const [subscripitonType, setSubscripitonType] = useState<NOTIFICATION_TYPE>(NOTIFICATION_TYPE.ALL);
  const [isLoadData, setLoadData] = useState(true);
  const [orderKeyword, setOrderKeyword] = useState<SortOrder>('asc');
  const [orderDate, setOrderDate] = useState<any>('desc');
  const [activeOrder, setActiveOrder] = useState<any>('date');
  const [rows, setRows] = useState<any>([]);
  const [isLoad, setLoad] = useState(true);
  const [showFrom, setShowFrom] = useState(0);
  const [subscriptionsList, setSubscriptionsList] = useState([]);
  const [filterBy, setFilterBy] = useState<any>();
  const [isInitialized, setIsInitialized] = useState(false);
  const showItems = 20;

  const parseUrlParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const keyword = urlParams.get('keyword');
    const documentSource = urlParams.get('document_source');

    if (keyword && documentSource) {
      const filterValue = {
        label: `${keyword} - ${documentSource}`,
        value: `${keyword}|||${documentSource}`
      };
      return filterValue;
    }
    return null;
  };

  const updateUrlWithFilter = (filter: any) => {
    if (filter && filter.value) {
      const [keyword, documentSource] = filter.value.split('|||');
      const url = new URL(window.location.href);
      url.searchParams.set('keyword', keyword);
      url.searchParams.set('document_source', documentSource);
      window.history.replaceState({}, '', url.toString());
    } else {
      const url = new URL(window.location.href);
      url.searchParams.delete('keyword');
      url.searchParams.delete('document_source');
      window.history.replaceState({}, '', url.toString());
    }
  };

  useEffect(() => {
    getSubscriptionList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (subscriptionsList?.length > 0) {
      const urlFilter = parseUrlParams();
      if (urlFilter) {
        setFilterBy(urlFilter);
        updateUrlWithFilter(urlFilter);
      }
      setIsInitialized(true);
    }
  }, [subscriptionsList]);

  const getSubscriptionList = async () => {
    const config = {
      params: {
        email: userEmail,
        action: 'subscription_list',
        records_num_from: 0,
        records_num_show: 500,
        subscription_status: 'All',
      },
    };
    try {
      setIsLoading(true);
      await axios
        .get(API_BASE_URL + '/AgencyTracker', config)
        .then((response) => {
          setSubscriptionsList(response.data.data);
        })
        .catch((error) => {
          CeresLogger.error({
            message: error instanceof Error ? error.message : 'Failed to load subscription',
            payload: { error },
          });
          toast.error(`Failed to load subscription.`);
        })
        .finally(() => setIsLoading(false));
    } catch (e) {
      CeresLogger.error({
        message: e instanceof Error ? e.message : String(e),
        payload: { e },
      });
    }
    return null;
  };

  const getSubjectTrack = async (fromPage = 0) => {
    setLoad(false);
    const keyword = filterBy ? filterBy.value.split('|||')[0] : '';
    const documentSource = filterBy ? filterBy.value.split('|||')[1] : '';
    const config = {
      params: {
        email: userEmail,
        action: 'notification_list',
        records_num_from: fromPage,
        records_num_show: showItems,
        subscription_keyword: keyword,
        document_source: documentSource,
        sorting: activeOrder === 'keyword' ? 'keyword-' + orderKeyword : 'document_date-' + orderDate,
      },
    };
    try {
      await axios
        .get(API_BASE_URL + '/AgencyTracker', config)
        .then((response) => {
          setRows(response.data);
        })
        .catch((error) => {
          CeresLogger.error({
            message: error instanceof Error ? error.message : String(error),
            payload: { error },
          });
        });
    } catch (e) {
      CeresLogger.error({
        message: e instanceof Error ? e.message : String(e),
        payload: { e },
      });
    }
    return null;
  };

  useEffect(() => {
    if (userEmail && !isLoading && isInitialized) {
      getSubjectTrack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBy, userEmail, isLoading, isInitialized]);

  const saveSubjectTrack = async () => {
    const config = {
      params: {
        email: userEmail,
        action: 'subscription_add',
        keyword: subscriptionData,
        document_source: subscripitonType,
      },
    };
    try {
      setFormValidation(true);
      setIsLoading(true);
      ReactGA.event({
        category: 'Search',
        action: 'Trackbot Agency Query',
        label: subscriptionData,
      });
      await axios
        .get(API_BASE_URL + '/AgencyTracker', config)
        .then((response) => {
          if (response.data.status === 'success add new keyword subscription') {
            toast.success(`You've successfully added keyword subscription`);
          }
          setSubscriptionData('');
          setSubscripitonType(NOTIFICATION_TYPE.ALL);
          setFormValidation(false);
          setShowFrom(0);
          getSubjectTrack();
          getSubscriptionList();
        })
        .catch((error) => {
          CeresLogger.error({
            message: error instanceof Error ? error.message : 'Failed to register subscription',
            payload: { error },
          });
          toast.error(`Failed to register subscription.`);
        })
        .finally(() => setIsLoading(false));
    } catch (e) {
      CeresLogger.error({
        message: e instanceof Error ? e.message : String(e),
        payload: { e },
      });
    }
    return null;
  };

  return (
    <PageRoot className="track-area">
      <Inner>
        <CustRow>
          <Col md={4}>
            <GlassCard>
              <UiKicker>Agency tracker</UiKicker>
              <Heading level={2} size="md" style={{ marginBottom: '1.1rem' }}>
                Track agency filings
              </Heading>
              <Form
                noValidate
                validated={isFormValidated}
                onSubmit={(e: React.FormEvent) => {
                  e.preventDefault();
                  const form = e.currentTarget as HTMLFormElement;
                  if (form && !form.checkValidity()) {
                    e.stopPropagation();
                    setFormValidation(true);
                    return;
                  }
                  saveSubjectTrack();
                }}
              >
                <Form.Group controlId="validationCustom02">
                  <Form.Control
                    required
                    value={subscriptionData}
                    onChange={(e: any) => setSubscriptionData(e.target.value)}
                    type="text"
                    placeholder="Words or subjects to track"
                    aria-label="Words/Subjects to Track"
                  />
                  <Form.Control.Feedback type="invalid">
                    Please enter required information for subscription.
                  </Form.Control.Feedback>
                </Form.Group>
                <SubscriptionTypeGroup className="form-group" controlId="validationCustom03">
                  <SubscriptionTypeRadioItem>
                    <InputGroup.Radio
                      value={NOTIFICATION_TYPE.ALL}
                      checked={subscripitonType === NOTIFICATION_TYPE.ALL}
                      onChange={(e: any) => {
                        if (e.target.checked) {
                          setSubscripitonType(NOTIFICATION_TYPE.ALL);
                        }
                      }}
                    />
                    <SubscriptionTypeText>All</SubscriptionTypeText>
                  </SubscriptionTypeRadioItem>
                  <SubscriptionTypeRadioItem>
                    <InputGroup.Radio
                      value={NOTIFICATION_TYPE.LDEQ}
                      checked={subscripitonType === NOTIFICATION_TYPE.LDEQ}
                      onChange={(e: any) => {
                        if (e.target.checked) {
                          setSubscripitonType(NOTIFICATION_TYPE.LDEQ);
                        }
                      }}
                    />
                    <SubscriptionTypeText>LDEQ</SubscriptionTypeText>
                  </SubscriptionTypeRadioItem>
                  <SubscriptionTypeRadioItem>
                    <InputGroup.Radio
                      value={NOTIFICATION_TYPE.TCEQ}
                      checked={subscripitonType === NOTIFICATION_TYPE.TCEQ}
                      onChange={(e: any) => {
                        if (e.target.checked) {
                          setSubscripitonType(NOTIFICATION_TYPE.TCEQ);
                        }
                      }}
                    />
                    <SubscriptionTypeText>TCEQ</SubscriptionTypeText>
                  </SubscriptionTypeRadioItem>
                </SubscriptionTypeGroup>
                <UiButton type="submit" disabled={isLoading} loading={isLoading}>
                  Subscribe
                </UiButton>
              </Form>
            </GlassCard>
            <SubscriptionListLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setShowSubscriptionList(true);
                setLoadData(true);
              }}
            >
              Manage subscriptions
            </SubscriptionListLink>
          </Col>

          <Col md={4}>
            <GlassCard>
              <Trackbot>
                <h3>Launch!</h3>
                <TrackbotRobo>
                  <img src="/ceres-robo.png" alt="TrackBot" />
                  <h4>TrackBot</h4>
                </TrackbotRobo>
              </Trackbot>
            </GlassCard>
          </Col>

          <Col md={4}>
            <GlassCard>
              <UiKicker>Notifications</UiKicker>
              <Heading level={2} size="md" className="cmn-section-title-tight">
                Email alerts
              </Heading>
              <EmailNotification userEmail={userEmail} />
            </GlassCard>
          </Col>
        </CustRow>
        <TrackingList>
          <ContentHeader>
            <h3>Tracking list</h3>
            <FilterArea>
              <span>Filter</span>
              <Filter
                subscriptionsList={subscriptionsList}
                filterBy={filterBy}
                setFilterBy={(filter: any) => {
                  setFilterBy(filter);
                  updateUrlWithFilter(filter);
                }}
              />
            </FilterArea>
          </ContentHeader>
          <NotificationContainer
            getSubjectTrack={getSubjectTrack}
            showItems={showItems}
            isLoad={isLoad}
            orderDate={orderDate}
            setLoad={setLoad}
            setOrderDate={setOrderDate}
            setOrderKeyword={setOrderKeyword}
            activeOrder={activeOrder}
            setActiveOrder={setActiveOrder}
            orderKeyword={orderKeyword}
            rows={rows}
            showFrom={showFrom}
            setShowFrom={setShowFrom}
            getSubscriptionList={getSubscriptionList}
          />
        </TrackingList>
      </Inner>
      <EntitySubscriptionsList
        onHide={() => setShowSubscriptionList(false)}
        show={showSubscriptionList}
        isLoadData={isLoadData}
        setLoadData={setLoadData}
        getSubjectTrack={getSubjectTrack}
        setShowFrom={setShowFrom}
        subscriptionsList={subscriptionsList}
        setSubscriptionsList={setSubscriptionsList}
      />
    </PageRoot>
  );
};

export default withAuthContext(NotificationPage);
